import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import { useIntl } from 'react-intl';
import MainAdUnitContainer from
'@fuww/components/src/Advertisement/MainAdUnitContainer';
import withQuery from '../../../../lib/withQuery';
import messages from '../../../../lib/messages';
import getArticlesQuery from '../../../../lib/queries/news/getArticlesQuery';
import getArticlesField from '../../../../lib/queries/news/getArticlesField';
import getSpotlightArticlesQuery
from '../../../../lib/queries/news/getSpotlightArticlesQuery';
import featured, { spotlightVariables } from
'../../../../lib/queries/news/variables/featured';
import AdUnit, { hasAdunit } from '../../../../lib/adunit';
import { featuredCategoryIds } from '../../../../lib/mainCategoryIds';
import SiteContext from '../../../SiteContext';
import hasMoreFeaturedNews, {
  hasMoreNews,
} from '../../../../lib/hasMoreFeaturedNews';
import getMoreNewsRows from '../../../../lib/getMoreNewsRows';
import loading from '../../../../lib/loading';

const FeaturedSection = dynamic(() => import('./FeaturedSection'), {
  ssr: true,
  loading,
});
const MoreFeaturedNews = dynamic(() => import('./MoreFeaturedNews'), {
  ssr: true,
  loading,
});
const MoreNewsSection = dynamic(() => import('../MoreNews'), {
  ssr: true,
  loading,
});

const Top = ({
  data,
  variables,
  field,
  spotlightNewsArticle,
  showCreatedAt,
  expandedSections,
  moreNewsVariables,
}) => {
  const intl = useIntl();
  const { locale, newsboard } = useContext(SiteContext);

  const featuredArticles = spotlightNewsArticle
    ? [spotlightNewsArticle, ...data[field]]
    : data[field];

  const notInIds = featuredArticles.map((item) => item.id);

  return (
    <>
      <FeaturedSection
        title={intl.formatMessage(messages.featured)}
        variables={variables}
        featuredArticles={featuredArticles}
        showCreatedAt={showCreatedAt}
      />
      {hasAdunit(newsboard) && (
        <MainAdUnitContainer>
          <AdUnit
            adUnit="home_leaderboard1"
            sizes={[[1, 1], [728, 90], [970, 90], [970, 250]]}
            minHeight="90px"
          />
        </MainAdUnitContainer>
      )}
      {expandedSections && hasMoreFeaturedNews(locale) && (
        <MoreFeaturedNews
          variables={{
            ...variables,
            offset: 4,
          }}
          showCreatedAt={showCreatedAt}
          notInIds={notInIds}
        />
      )}
      {hasMoreNews(newsboard) && (
        <MoreNewsSection
          variables={{
            ...moreNewsVariables,
            notInIds,
          }}
          showCreatedAt={showCreatedAt}
          rows={expandedSections ? getMoreNewsRows(locale) : undefined}
        />
      )}
    </>
  );
};

Top.propTypes = {
  data: PropTypes.shape({}).isRequired,
  variables: PropTypes.shape({}).isRequired,
  moreNewsVariables: PropTypes.shape({}),
  field: PropTypes.string.isRequired,
  spotlightNewsArticle: PropTypes.shape({}),
  expandedSections: PropTypes.bool.isRequired,
  showCreatedAt: PropTypes.bool,
};

Top.defaultProps = {
  moreNewsVariables: null,
  spotlightNewsArticle: null,
  showCreatedAt: false,
};

const TopNewsWithQuery = withQuery(Top);
const errorMessage = 'Error loading news articles...';

const TopNewsSection = ({
  variables,
  showCreatedAt,
  expandedSections,
  moreNewsVariables,
}) => {
  const { prefix, hasLocalNews, newsboard } = useContext(SiteContext);
  const articleField = getArticlesField(prefix, hasLocalNews);
  const spotlightArticlesQuery = getSpotlightArticlesQuery(articleField);
  const featuredSpotlightVariables = {
    ...spotlightVariables,
    newsboards: [newsboard],
    categoryIds: featuredCategoryIds,
    ...variables,
  };

  const TopNewsWithData = ({ data }) => {
    const { tag } = data;
    const { [articleField]: [spotlightNewsArticle] } = tag;
    const notInIds = spotlightNewsArticle ? [spotlightNewsArticle.id] : [];

    const articlesField = getArticlesField(prefix, hasLocalNews);
    const articlesQuery = getArticlesQuery(articlesField);
    const featuredVariables = {
      ...featured,
      newsboards: [newsboard],
      notInIds,
      categoryIds: featuredCategoryIds,
      ...variables,
    };

    return (
      <TopNewsWithQuery
        query={articlesQuery}
        variables={featuredVariables}
        errorMessage={errorMessage}
        field={articlesField}
        spotlightNewsArticle={spotlightNewsArticle}
        showCreatedAt={showCreatedAt}
        expandedSections={expandedSections}
        moreNewsVariables={moreNewsVariables}
      />
    );
  };

  TopNewsWithData.propTypes = {
    data: PropTypes.shape({ tag: PropTypes.shape({}) }).isRequired,
  };

  const TopNewsWithDataWithSpotlightQuery = withQuery(TopNewsWithData);

  return (
    <TopNewsWithDataWithSpotlightQuery
      query={spotlightArticlesQuery}
      variables={featuredSpotlightVariables}
      errorMessage={errorMessage}
      hasLocalNews={hasLocalNews}
    />
  );
};

TopNewsSection.propTypes = {
  variables: PropTypes.shape({}),
  moreNewsVariables: PropTypes.shape({}),
  expandedSections: PropTypes.bool.isRequired,
  showCreatedAt: PropTypes.bool,
};

TopNewsSection.defaultProps = {
  variables: null,
  moreNewsVariables: null,
  showCreatedAt: false,
};

export default TopNewsSection;
